import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { DataTable, fmtDate } from './usageShared';

/**
 * Which employee uses which feature, from the server-side usage tracker.
 * "Actions" are things done (create, update, delete); "views" are things
 * opened. Covers web and mobile alike.
 */
const FeatureUsagePanel = ({ from, to }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let live = true;
        setError('');
        api.get('/admin-usage/features', { params: { from, to } })
            .then(r => { if (live) setData(r.data); })
            .catch(e => { if (live) setError(e.response?.data?.message || 'Could not load feature usage.'); });
        return () => { live = false; };
    }, [from, to]);

    const featureNames = useMemo(() => (data ? data.features.map(f => f.feature) : []), [data]);

    if (error) return <div className="ur-empty">{error}</div>;
    if (!data) return <div className="ur-empty">Loading...</div>;

    const since = data.trackingSince
        ? `Tracking started ${new Date(data.trackingSince).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}; earlier days have no data.`
        : 'No usage recorded yet. Tracking starts from this release.';

    return (
        <>
            <div className="ur-hint">{since}</div>

            <h3 style={{ margin: '4px 0 8px' }}>By feature</h3>
            <DataTable
                key="features"
                rows={data.features}
                searchKeys={[r => r.feature]}
                csvName="usage-by-feature"
                empty="No feature usage recorded in this period."
                cols={[
                    { key: 'feature', label: 'Feature' },
                    { key: 'users', label: 'People', num: true, render: r => `${r.users} (${r.usersPct}%)` },
                    { key: 'writes', label: 'Actions', num: true },
                    { key: 'reads', label: 'Views', num: true },
                    { key: 'web', label: 'Web', num: true },
                    { key: 'mobile', label: 'Mobile', num: true }
                ]}
            />

            <h3 style={{ margin: '18px 0 8px' }}>By employee</h3>
            <div className="ur-hint">Each feature column shows actions / views. Blank means unused in this period.</div>
            <DataTable
                key="users"
                rows={data.users}
                searchKeys={[r => r.name, r => r.department]}
                csvName="usage-feature-matrix"
                empty="No employees found."
                cols={[
                    { key: 'name', label: 'Employee', render: r => <><strong>{r.name}</strong> <span className="ur-pill">{r.department}</span></> },
                    { key: 'actions', label: 'Actions', num: true },
                    { key: 'featuresUsed', label: 'Features used', num: true },
                    { key: 'web', label: 'Web', num: true },
                    { key: 'mobile', label: 'Mobile', num: true },
                    { key: 'lastActive', label: 'Last active', render: r => fmtDate(r.lastActive) },
                    ...featureNames.map(name => ({
                        key: `f:${name}`,
                        label: name,
                        num: true,
                        value: r => (r.features[name] ? r.features[name].writes : -1),
                        render: r => (r.features[name] ? `${r.features[name].writes} / ${r.features[name].reads}` : '')
                    }))
                ]}
            />
        </>
    );
};

export default FeatureUsagePanel;
